import { Typography, TypographyProps, useTheme } from "@mui/material";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";

type RenderContentProps = Omit<TypographyProps, "children"> & {
  content?: string | null;
};

// Renders HTML strings from i18n translations.
// Interchangeable with Typography — accepts the same props.
// Use only when content contains inline HTML (strong, a, em, etc.).
const RenderContent = ({ content, sx, variant = "body2", ...rest }: RenderContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const newTabLabel = t("buttons.opensInNewTab");

  if (!content) return null;

  // Sanitize: allow only safe inline formatting tags and attributes
  const sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ["strong", "em", "s", "u", "br", "a", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "style", "class"]
  });

  // Inject visually-hidden "(opens in new tab)" after external links
  const processed = sanitized.replace(
    /(<a\b[^>]*\btarget="_blank"[^>]*>)([\s\S]*?)(<\/a>)/g,
    `$1$2<span style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0"> (${newTabLabel})</span>$3`
  );

  return (
    <Typography
      variant={variant}
      sx={[
        {
          "& h1": { ...theme.typography.h1, margin: 0 },
          "& h2": { ...theme.typography.h2, margin: 0 },
          "& h3": { ...theme.typography.h3, margin: 0 },
          "& h4": { ...theme.typography.h4, margin: 0 },
          "& h5": { ...theme.typography.h5, margin: 0 },
          "& h6": { ...theme.typography.h6, margin: 0 },
          "& p": { ...theme.typography.body2, margin: 0 },
          "& a": { color: "inherit" }
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
      dangerouslySetInnerHTML={{ __html: processed }}
      {...rest}
    />
  );
};

export default RenderContent;
